/**
 * useReportingCurrency — derives the currency context for the Dashboard.
 *
 * Input is the currently-selected location set; output is one of three shapes:
 *
 * - `{ kind: 'single', currency: ISO-4217, locale }`
 *     All selected locations share a currency. Render with this currency
 *     everywhere.
 *
 * - `{ kind: 'mixed', currencies: ['USD','JPY'], byLocation: { 12: 'USD', 17: 'JPY' } }`
 *     The selection spans multiple source currencies. Caller can request a
 *     server-side target currency for converted portfolio totals.
 *
 * - `{ kind: 'empty' }`
 *     No locations available yet (skeleton state).
 *
 * Critical invariant: `location.currency ?? 'USD'` everywhere. NULL collapses
 * into the USD bucket so an org that has never set a currency keeps today's
 * behavior.
 */

import { useMemo } from 'react';
import { CURRENCY_LOCALE } from '../utils/currency';

function normalize(currency) {
  if (!currency || typeof currency !== 'string') return 'USD';
  const upper = currency.toUpperCase();
  return /^[A-Z]{3}$/.test(upper) ? upper : 'USD';
}

/**
 * @param {Array<{id: number, currency?: string | null}>} availableLocations
 * @param {Array<string|number>} selectedLocations - location IDs, or `['all']`
 */
export default function useReportingCurrency(availableLocations, selectedLocations) {
  return useMemo(() => {
    if (!Array.isArray(availableLocations) || availableLocations.length === 0) {
      return { kind: 'empty' };
    }

    const isAll = !Array.isArray(selectedLocations)
      || selectedLocations.length === 0
      || selectedLocations.includes('all');
    const selectedIds = isAll
      ? availableLocations.map((l) => l.id)
      : selectedLocations.map((v) => Number(v)).filter(Number.isFinite);

    const byLocation = {};
    const set = new Set();
    for (const loc of availableLocations) {
      if (!selectedIds.includes(loc.id)) continue;
      const ccy = normalize(loc.currency);
      byLocation[loc.id] = ccy;
      set.add(ccy);
    }

    if (set.size === 0) {
      // Selected IDs didn't match any known location (likely a transient state).
      return { kind: 'single', currency: 'USD', locale: CURRENCY_LOCALE.USD };
    }

    if (set.size === 1) {
      const currency = [...set][0];
      return { kind: 'single', currency, locale: CURRENCY_LOCALE[currency] || 'en-US' };
    }

    return {
      kind: 'mixed',
      currencies: [...set].sort(),
      byLocation,
    };
  }, [availableLocations, selectedLocations]);
}
